import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, ClipboardList, PlusCircle, Users, CalendarOff, Settings, LogOut, Menu, Map, CalendarRange, Layers, MessageSquare, Contact, QrCode, MessageCircle, Route, HardHat, Package } from "lucide-react";
import { useUnread } from "@/components/Chat";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth, isAdmin } from "@/lib/auth";
import { AdminBell, AdminPushBanner } from "@/components/AdminNotifications";
import { PushToggle } from "@/components/PushToggle";
import { BrandLogo } from "@/components/Brand";
import { PasswordChangeGate } from "@/components/PasswordChangeGate";
import { api } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true, id: "dashboard", hr: true },
  { to: "/admin/interventi", label: "Interventi", Icon: ClipboardList, id: "jobs", perm: ["jobs_view", "jobs_edit"] },
  { to: "/admin/cantieri", label: "Cantieri", Icon: HardHat, id: "cantieri", perm: ["cantieri"] },
  { to: "/admin/pianificazione", label: "Pianificazione settimana", Icon: CalendarRange, id: "planner", perm: ["cantieri"] },
  { to: "/admin/mappa", label: "Mappa", Icon: Map, id: "map" },
  { to: "/admin/nuovo", label: "Nuovo appuntamento", Icon: PlusCircle, id: "new-job", perm: ["jobs_edit"] },
  { to: "/admin/contatti", label: "Contatti", Icon: Contact, id: "leads", perm: ["leads"] },
  { to: "/admin/timbrature", label: "Timbrature", Icon: QrCode, id: "punches", perm: ["punches"] },
  { to: "/admin/percorsi", label: "Percorsi", Icon: Route, id: "routes" },
  { to: "/admin/whatsapp", label: "WhatsApp", Icon: MessageCircle, id: "whatsapp", perm: ["whatsapp"] },
  { to: "/admin/messaggi", label: "Messaggi", Icon: MessageSquare, id: "messages" },
  { to: "/admin/tecnici", label: "Utenti", Icon: Users, id: "technicians" },
  { to: "/admin/assenze", label: "Assenze", Icon: CalendarOff, id: "absences", hr: true },
  { to: "/admin/presenze", label: "Calendario presenze", Icon: CalendarRange, id: "attendance", hr: true },
  { to: "/admin/tipi-intervento", label: "Tipi di intervento", Icon: Layers, id: "service-types" },
  { to: "/admin/ricambi", label: "Catalogo ricambi", Icon: Package, id: "parts" },
  { to: "/admin/impostazioni", label: "Orari e regole", Icon: Settings, id: "settings" },
];

function useNavBadges() {
  const [b, setB] = useState({});
  const { pathname } = useLocation();
  useEffect(() => {
    const load = () => api.get("/nav-badges").then((r) => setB(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [pathname]);
  return b;
}

function NavList({ prefix = "" }) {
  const { user, logout } = useAuth();
  const unread = useUnread(isAdmin(user));
  const badges = useNavBadges();
  return (
    <div className="flex min-h-full flex-col">
      <nav className="space-y-1">
        {(user?.role === "responsabile" || user?.role === "tecnico") && (
          <NavLink to="/tecnico" data-testid={`${prefix}nav-field-app`} className="flex items-center gap-3 rounded-xl border border-white/10 px-3 py-2.5 text-sm font-medium text-[#a2ca6b] hover:bg-white/5">
            <HardHat className="h-4 w-4" /> App campo (timbrature e cantieri)
          </NavLink>
        )}
        {NAV.filter((n) => isAdmin(user) || n.hr || n.perm?.some((p) => user?.permissions?.includes(p))).map(({ to, label, Icon, end, id }) => (
          <NavLink key={to} to={to} end={end} data-testid={`${prefix}nav-${id}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-[#a2ca6b] text-[#0f1710]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" /> {label}
            {id === "messages" && unread > 0 && <span className="ml-auto rounded-full bg-[#61a229] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`${prefix}nav-messages-unread`}>{unread}</span>}
            {id !== "messages" && badges[to] > 0 && <span className="ml-auto min-w-5 rounded-full bg-orange-500 px-2 py-0.5 text-center text-[10px] font-bold text-white" data-testid={`${prefix}nav-${id}-badge`}>{badges[to] > 99 ? "99+" : badges[to]}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto border-t border-white/10 pt-4 pb-2">
        <div className="mb-3 flex items-center gap-2"><AdminBell dark /><PushToggle dark /></div>
        <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
        <p className="truncate text-xs text-slate-400">{user?.email}</p>
        <button onClick={logout} data-testid={`${prefix}logout-button`}
          className="mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const badges = useNavBadges();
  const { user } = useAuth();
  const unread = useUnread(isAdmin(user));
  const any = unread > 0 || Object.values(badges).some((n) => n > 0);
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:flex sticky top-0 h-screen flex-col gap-8 overflow-y-auto bg-[#0f1710] p-5">
        <div className="flex flex-col gap-2">
          <img src="/brand/logo.png" alt="Madonna Store" className="h-7 w-auto self-start" />
          <p className="font-display text-sm font-bold tracking-[0.2em] text-white">MADONNA <span className="text-[#a2ca6b]">STORE</span></p>
        </div>
        <NavList />
      </aside>
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b bg-white/90 px-4 py-3 backdrop-blur-md">
        <BrandLogo to="/admin" />
        <div className="flex items-center gap-1">
        <AdminBell />
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="relative" data-testid="admin-mobile-menu-button"><Menu className="h-4 w-4" />
              {any && <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-orange-500" data-testid="admin-mobile-menu-dot" />}</Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 overflow-y-auto border-0 bg-[#0f1710] p-5 pt-12">
            <NavList prefix="mobile-" />
          </SheetContent>
        </Sheet>
        </div>
      </header>
      <main className="min-w-0 px-4 py-6 sm:px-8 sm:py-10">
        <PasswordChangeGate><AdminPushBanner /><Outlet /></PasswordChangeGate>
      </main>
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between rise">
    <div>
      <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#61a229]">{eyebrow}</p>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
    </div>
    {children}
  </div>
);
